/**
 * Shared state for the AI Assistant module's preview UI.
 *
 * There is no assistant backend, token wallet or conversation store yet, so
 * everything the three tabs show that isn't derived from the caller's real
 * session is sample data that lives in this module's `sample/` files and is
 * labelled «نمونه» in the UI. What IS real here:
 *   - the data scope (which of the five modules the caller may read), taken
 *     from the caller's own capabilities (`useCapabilities`, DENY-first) —
 *     the assistant "never sees more than the user does";
 *   - the first name, the tenant base path and the tenant-admin flag.
 * What is sample: the token balance, conversations, answers, wallet history,
 * packages and usage report.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useCapabilities } from '../../lib/capabilities.js';
import { useApiGet } from '../../lib/useApiGet.js';
import { tenantBasePath } from '../../lib/tenant.js';
import type { OwnProfileResponse } from '../../lib/types/membership.js';

export type AiScopeKey = 'sales' | 'inventory' | 'procurement' | 'accounting' | 'projects';

export interface AiScopeItem {
  readonly key: AiScopeKey;
  readonly label: string;
  /** English tag used by the design's frame labels («SALES»…). */
  readonly granted: boolean;
}

/** Capability keys (any one grants the module); the backend remains the authority. */
const SCOPE_RULES: readonly { key: AiScopeKey; label: string; any: readonly string[] }[] = [
  {
    key: 'sales',
    label: 'فروش',
    any: ['sales.order.read', 'sales.invoice.read', 'crm.opportunity.read'],
  },
  { key: 'inventory', label: 'انبار', any: ['inventory.stock.read', 'inventory.movement.read'] },
  {
    key: 'procurement',
    label: 'تدارکات',
    any: ['procurement.order.read', 'procurement.request.read'],
  },
  {
    key: 'accounting',
    label: 'حسابداری',
    any: [
      'accounting.journal-entry.read',
      'accounting.ar-open-item.read',
      'accounting.ap-open-item.read',
    ],
  },
  { key: 'projects', label: 'پروژه', any: ['projects.project.read', 'projects.task.read'] },
];

/**
 * States the backend would decide (suspension, missing permission, upstream
 * error) cannot occur in a preview, so the layout lets the viewer force them
 * to see the corresponding frame. `none` = normal behaviour.
 */
export type AiForcedState = 'none' | 'suspended' | 'not-permitted' | 'error';

export interface AiTokens {
  /** Remaining tokens (sample). */
  readonly balance: number;
  /** Tokens bought in the current period — the progress bar's denominator (sample). */
  readonly purchased: number;
}

export interface AiPreviewValue {
  readonly slug: string;
  /** Tenant base path, e.g. `/t/kimia`. */
  readonly base: string;
  readonly firstName: string;
  readonly fullName: string;
  readonly isTenantAdmin: boolean;
  /** May buy tokens (tenant.subscription.manage). */
  readonly canPurchase: boolean;
  readonly scope: readonly AiScopeItem[];
  readonly tokens: AiTokens;
  /** Deduct tokens after an answer (floors at 0). */
  readonly spendTokens: (amount: number) => void;
  /** Credit tokens after a (sample) purchase. */
  readonly addTokens: (amount: number) => void;
  readonly forcedState: AiForcedState;
  readonly setForcedState: (state: AiForcedState) => void;
}

const AiPreviewContext = createContext<AiPreviewValue | null>(null);

export const INITIAL_SAMPLE_TOKENS: AiTokens = { balance: 23_000, purchased: 100_000 };

export function AiPreviewProvider({ children }: { children: ReactNode }) {
  const { slug = '' } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const caps = useCapabilities();
  const profile = useApiGet<OwnProfileResponse>(slug ? `${base}/api/v1/me` : null, [base]);
  const [tokens, setTokens] = useState<AiTokens>(INITIAL_SAMPLE_TOKENS);
  const [forcedState, setForcedState] = useState<AiForcedState>('none');

  const spendTokens = useCallback(
    (amount: number) =>
      setTokens((t) => ({
        ...t,
        balance: Math.max(0, t.balance - Math.max(0, Math.round(amount))),
      })),
    [],
  );
  const addTokens = useCallback(
    (amount: number) =>
      setTokens((t) => ({
        balance: t.balance + Math.max(0, Math.round(amount)),
        purchased: t.purchased + Math.max(0, Math.round(amount)),
      })),
    [],
  );

  const value = useMemo<AiPreviewValue>(() => {
    const firstName = profile.status === 'ready' ? (profile.data.firstName?.trim() ?? '') : '';
    const lastName = profile.status === 'ready' ? (profile.data.lastName?.trim() ?? '') : '';
    return {
      slug,
      base,
      firstName: firstName || 'کاربر',
      fullName: [firstName, lastName].filter(Boolean).join(' ') || 'کاربر',
      isTenantAdmin: caps.isTenantAdmin,
      canPurchase: caps.hasCapability('tenant.subscription.manage'),
      scope: SCOPE_RULES.map((rule) => ({
        key: rule.key,
        label: rule.label,
        granted: rule.any.some((key) => caps.hasCapability(key)),
      })),
      tokens,
      spendTokens,
      addTokens,
      forcedState,
      setForcedState,
    };
  }, [slug, base, profile, caps, tokens, spendTokens, addTokens, forcedState]);

  return <AiPreviewContext.Provider value={value}>{children}</AiPreviewContext.Provider>;
}

export function useAiPreview(): AiPreviewValue {
  const value = useContext(AiPreviewContext);
  if (!value) throw new Error('useAiPreview must be used inside <AiPreviewProvider>');
  return value;
}
